import React, { useState, useEffect, useCallback } from "react";
import { InputAdornment } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { FILTERS, debouncedMethod, generateDocumentFilterName, generateFilterTitle } from "../../../../utils/utils";
import {
	Filter,
	icon,
	CustomIconButton,
	FilterSearchList,
	CircularLoader,
	FilterInfiniteList,
	CustomTextField,
} from "../../../../../../common/components";
import { isDeepEqual } from "../../../../../../common/utils";
import { translate } from "../../../../../../common/providers";
import { setFilters, setPage, setTempFilters } from "../../../../slice/dsi-slice";

export default function UserFilters({ companies = [], projectsRequest, onSearch }) {
	const [content, setContent] = useState("");
	const [filterCompany, setFilterCompany] = useState("");
	const [filteredCompanies, setFilteredCompanies] = useState(companies);
	const dispatch = useDispatch();
	const page = useSelector(({ dsi }) => dsi.page);
	const limit = useSelector(({ dsi }) => dsi.limit);
	const tempFilters = useSelector(({ dsi }) => dsi.tempFilters);
	const tempTotalElements = useSelector(({ dsi }) => dsi.tempTotalElements);
	const IsLoadingUsers = useSelector(({ dsi }) => dsi.IsLoadingUsers);
	const filters = useSelector(({ dsi }) => dsi.filters);
	const needApply = (filterKey) => !isDeepEqual(tempFilters[filterKey], filters[filterKey]);
	useEffect(() => {
		setContent(filters[FILTERS.SEARCH]);
	}, [filters]);

	const filterCompanies = useCallback((tree, query) => {
		if (!query) {
			return tree;
		}

		return tree
			.map((data) => {
				const filteredChildren = data.children ? filterCompanies(data.children, query) : [];
				const matches =
					(data.companyName?.toLowerCase() || "").includes(query.toLowerCase()) ||
					(data.shortName?.toLowerCase() || "").includes(query.toLowerCase());

				if (matches || filteredChildren.length > 0) {
					return { ...data, children: filteredChildren };
				}
				return null;
			})
			.filter(Boolean);
	}, []);
	useEffect(() => {
		const updatedCompanies = filterCompanies(companies, filterCompany);
		setFilteredCompanies(updatedCompanies);
	}, [filterCompany, companies, filterCompanies]);
	const resetTempFilters = () => {
		dispatch(setTempFilters(filters));
	};
	const resetTempFilter = (key, display) => {
		const newTempFilters = { ...filters, [key]: [] };
		onSearch({ page: 0, limit, filters: newTempFilters, display });
		dispatch(setTempFilters({ ...filters, [key]: [] }));
		if (display) {
			dispatch(setPage(0));
			dispatch(setFilters({ ...filters, [key]: [] }));
		}
	};
	const handleSearch = (searchFilter) => {
		if (needApply(searchFilter)) {
			dispatch(setFilters(tempFilters));
		}
	};
	const handleCheckItem = (list, key) => {
		const newTempFilters = { ...filters, [key]: list };
		onSearch({ page, limit, filters: newTempFilters, display: false });
	};
	const handleContentChange = (e) => {
		const newValue = e.target.value;
		setContent(newValue);
		debouncedMethod(onSearch, { filters: { ...filters, search: newValue }, page: 0, limit, display: true });
	};
	const handleResetContent = () => {
		setContent("");
		debouncedMethod(onSearch, { filters: { ...filters, search: "" }, page: 0, limit, display: true });
		dispatch(setPage(0));
	};
	const handleSearchCompanies = (e) => setFilterCompany(e?.target?.value || "");
	const handleClearSearchCompanies = () => setFilterCompany("");
	const renderCloseLabel = (searchFilter) => {
		if (IsLoadingUsers) {
			return <CircularLoader color="inherit" size={25} />;
		}
		if (needApply(searchFilter)) {
			return translate("header.filter.apply-button", { resultsNb: tempTotalElements });
		}
		return translate("common:btn.close");
	};
	return (
		<>
			<Filter
				buttonColor={needApply(FILTERS.COMPANIES) ? "primary" : "secondary"}
				closeLabel={renderCloseLabel(FILTERS.COMPANIES)}
				disableClose={IsLoadingUsers}
				isActive={Object.keys(filters[FILTERS.COMPANIES]).length > 0}
				label={generateDocumentFilterName(filters[FILTERS.COMPANIES], translate("header.filter.companies"))}
				title={generateFilterTitle(
					translate("header.filter.companies"),
					(tempFilters[FILTERS.COMPANIES] || []).length
				)}
				onClear={() => resetTempFilter(FILTERS.COMPANIES, true)}
				onClose={() => handleSearch(FILTERS.COMPANIES)}
				onOpen={resetTempFilters}
			>
				<FilterSearchList
					defaultInputValue={filterCompany}
					initChecked={tempFilters[FILTERS.COMPANIES]}
					rowKey="id"
					rowLabelKey="companyName"
					rows={filteredCompanies}
					searchPlaceholder={translate("header.filter.companies")}
					searchResultCount={filteredCompanies.length}
					onChangeSearch={handleSearchCompanies}
					onClear={() => resetTempFilter(FILTERS.COMPANIES, false)}
					onClearSearch={handleClearSearchCompanies}
					onClickRow={(list) => handleCheckItem(list, FILTERS.COMPANIES)}
				/>
			</Filter>
			<Filter
				buttonColor={needApply(FILTERS.PROJECTS) ? "primary" : "secondary"}
				closeLabel={renderCloseLabel(FILTERS.PROJECTS)}
				disableClose={IsLoadingUsers}
				isActive={Object.keys(filters[FILTERS.PROJECTS]).length > 0}
				label={generateDocumentFilterName(filters[FILTERS.PROJECTS], translate("header.filter.projects"))}
				title={generateFilterTitle(
					translate("header.filter.projects"),
					(tempFilters[FILTERS.PROJECTS] || []).length
				)}
				onClear={() => resetTempFilter(FILTERS.PROJECTS, true)}
				onClose={() => handleSearch(FILTERS.PROJECTS)}
				onOpen={resetTempFilters}
			>
				<FilterInfiniteList
					initChecked={tempFilters[FILTERS.PROJECTS]}
					request={projectsRequest}
					rowKey="id"
					rowLabelKey="name"
					searchPlaceholder={translate("header.filter.projects")}
					searchResultCount={tempTotalElements}
					onClear={() => resetTempFilter(FILTERS.PROJECTS, false)}
					onClickRow={(list) => handleCheckItem(list, FILTERS.PROJECTS)}
				/>
			</Filter>
			<CustomTextField
				InputProps={
					content
						? {
								endAdornment: (
									<InputAdornment position="end">
										<CustomIconButton
											icon={icon.faTimesCircleRegular}
											onClick={handleResetContent}
										/>
									</InputAdornment>
								),
						  }
						: {}
				}
				placeholder={translate("header.input.placeholder.search-text-comment")}
				size="small"
				value={content}
				variant="outlined"
				onChange={handleContentChange}
			/>
		</>
	);
}
